import { useEffect, useState } from "react";
import { BRAND } from "@brand";
import dayjs from "dayjs";
import masLogo from "../assets/mas-logo.svg";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import { useAppState } from "../AppState";
import { api } from "../lib/api";
import {
  AddrLink,
  BLOCK_STATUS,
  BlockLink,
  ErrorMsg,
  Loading,
  OpKindPill,
  OpLink,
  Panel,
  SlotLink,
} from "../components/Bits";
import { SearchForm } from "../components/Layout";
import { LiveDag } from "../components/LiveDag";
import {
  firstIncludedSlot,
  formatRelative,
  slotTimestampMs,
} from "../lib/format";
import { MASSA_API_URL } from "../lib/config";
import type { Envelope, MassaInfo, StoredOperation } from "../lib/types";
import { useChainParams } from "../hooks/useChainParams";
import { useSseFrames } from "../hooks/useSseFrames";

/** Rows in each "Latest …" panel; the full lists are one click away. */
const HOME_ROWS = 10;

export function Home() {
  const { client, network } = useAppState();
  const params = useChainParams();
  const queryClient = useQueryClient();
  const recentOpsKey = ["recent-ops", network, client.endpoints(), HOME_ROWS];
  const recentOps = useQuery({
    queryKey: recentOpsKey,
    queryFn: () => api.recentOps(client, HOME_ROWS, null),
    // New ops are pushed by /v1/stream/operations below; this slow refetch
    // only heals stream gaps. The table shows no exec/final status, so
    // nothing visible goes stale in between.
    refetchInterval: 60_000,
  });
  // Prepend each newly ingested op to the cached list.
  useSseFrames<StoredOperation>(
    client,
    "/v1/stream/operations",
    "op_seen",
    (op) =>
      queryClient.setQueryData<Envelope<StoredOperation[]>>(recentOpsKey, (prev) =>
        !prev || prev.data.some((o) => o.id === op.id)
          ? prev
          : { ...prev, data: [op, ...prev.data].slice(0, HOME_ROWS) },
      ),
    true,
  );
  // Same poll period as the Blocks page; the DAG above is the real-time view.
  const recentBlocks = useQuery({
    queryKey: ["recent-blocks", network, client.endpoints(), HOME_ROWS],
    queryFn: () => api.recentBlocks(client, HOME_ROWS),
    refetchInterval: 10_000,
  });

  return (
    <>
      <Helmet>
        <title>{`${BRAND.title} — ${network}`}</title>
      </Helmet>

      <section className="flex flex-col items-center text-center gap-5 sm:gap-6 pb-8 sm:pb-10">
        <h1 className="text-4xl sm:text-6xl font-semibold tracking-tighter leading-[1.05]">
          {BRAND.hero.title}
        </h1>
        <p className="text-muted text-base sm:text-lg max-w-xl leading-relaxed">
          {BRAND.hero.subtitle}
        </p>
        <div className="w-full max-w-3xl">
          <SearchForm large />
        </div>
      </section>

      <MassaStats network={network} />

      <GetStarted />

      <div className="mb-6">
        <LiveDag client={client} params={params} windowSec={90} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        <Panel
          title="Latest blocks"
          action={
            <Link to="/blocks" className="text-sm font-medium no-underline">
              View all →
            </Link>
          }
        >
          {recentBlocks.isLoading ? (
            <Loading />
          ) : recentBlocks.isError ? (
            <ErrorMsg err={recentBlocks.error} />
          ) : (recentBlocks.data?.data.length ?? 0) === 0 ? (
            <div className="text-muted text-sm">No blocks yet.</div>
          ) : (
            <div className="overflow-x-auto -mx-3 sm:mx-0">
              <table className="w-full text-sm min-w-[520px]">
                <thead className="text-muted text-xs">
                  <tr>
                    <th className="text-left font-medium py-2 px-2">Block</th>
                    <th className="text-left font-medium py-2 px-2">Creator</th>
                    <th className="text-right font-medium py-2 px-2">Ops</th>
                    <th className="text-left font-medium py-2 px-2">Status</th>
                    <th className="text-right font-medium py-2 px-2">Produced</th>
                  </tr>
                </thead>
                <tbody>
                  {recentBlocks.data!.data.map((b) => {
                    const st = BLOCK_STATUS[b.status] ?? BLOCK_STATUS.discarded;
                    return (
                      <tr key={b.id} className="border-t border-border">
                        <td className="py-2 px-2">
                          <BlockLink id={b.id} />
                          <div className="text-xs">
                            <SlotLink slot={b.slot} />
                          </div>
                        </td>
                        <td className="px-2">
                          <AddrLink addr={b.creator} />
                        </td>
                        <td className="px-2 text-right tabular-nums">{b.operation_ids.length}</td>
                        <td className="px-2">
                          <span className="inline-flex items-center gap-2 text-[13px] whitespace-nowrap">
                            <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                            {st.label}
                          </span>
                        </td>
                        <td className="px-2 text-right text-muted text-xs whitespace-nowrap">
                          {formatRelative(slotTimestampMs(b.slot, params))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel
          title="Latest operations"
          action={
            <Link to="/operations" className="text-sm font-medium no-underline">
              View all →
            </Link>
          }
        >
          {recentOps.isLoading ? (
            <Loading />
          ) : recentOps.isError ? (
            <ErrorMsg err={recentOps.error} />
          ) : (recentOps.data?.data.length ?? 0) === 0 ? (
            <div className="text-muted text-sm">No operations yet.</div>
          ) : (
            <div className="overflow-x-auto -mx-3 sm:mx-0">
              <table className="w-full text-sm min-w-[520px]">
                <thead className="text-muted text-xs">
                  <tr>
                    <th className="text-left font-medium py-2 px-2">Operation</th>
                    <th className="text-left font-medium py-2 px-2">Type</th>
                    <th className="text-left font-medium py-2 px-2">From</th>
                    <th className="text-left font-medium py-2 px-2">To</th>
                    <th
                      className="text-right font-medium py-2 px-2 whitespace-nowrap"
                      title="Wall-clock time of the slot in which the operation was first included in a block."
                    >
                      Included
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {recentOps.data!.data.map((op) => {
                    const incSlot = firstIncludedSlot(op);
                    const incTs = incSlot
                      ? slotTimestampMs(incSlot, params)
                      : null;
                    return (
                      <tr key={op.id} className="border-t border-border">
                        <td className="py-3 px-2">
                          <OpLink id={op.id} />
                        </td>
                        <td className="px-2 align-middle">
                          <OpKindPill kind={op.kind} />
                        </td>
                        <td className="px-2">
                          <AddrLink addr={op.creator} />
                        </td>
                        <td className="px-2">
                          {op.target ? <AddrLink addr={op.target} /> : "—"}
                        </td>
                        <td className="px-2 align-middle text-right text-muted text-xs whitespace-nowrap">
                          {incTs != null ? formatRelative(incTs) : "pending"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
    </>
  );
}

function GetStarted() {
  if (BRAND.getStarted.length === 0) return null;
  return (
    <section className="mb-6">
      <h2 className="text-lg font-semibold mb-4">Get started with {BRAND.name}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {BRAND.getStarted.map((g) => (
          <a
            key={g.href}
            href={g.href}
            target="_blank"
            rel="noreferrer"
            className="card group flex items-center gap-4 no-underline text-fg hover:border-muted/40 transition-colors"
          >
            <span className="w-11 h-11 shrink-0 rounded-xl grid place-items-center" style={{ background: `${g.color}1a`, color: g.color }}>
              <span className="w-3 h-3 rotate-45 rounded-[3px] bg-current" />
            </span>
            <span className="flex flex-col gap-1 min-w-0">
              <span className="font-semibold">{g.name}</span>
              <span className="text-sm text-muted">{g.desc}</span>
            </span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-auto shrink-0 text-muted group-hover:text-fg transition-colors" aria-hidden="true">
              <path d="M7 17 17 7M8 7h9v9" />
            </svg>
          </a>
        ))}
      </div>
    </section>
  );
}

const MAS = (n: number) =>
  n.toLocaleString("en-US", { maximumFractionDigits: 3 });

/**
 * Supply, staking and cycle tiles, same content as explorer.massa.net.
 * Data comes from the Massa Labs API (mainnet only), not the indexer.
 */
function MassaStats({ network }: { network: string }) {
  const enabled = network === "mainnet" && !!MASSA_API_URL;
  const info = useQuery({
    queryKey: ["massa-info"],
    queryFn: api.massaInfo,
    enabled,
    refetchInterval: 30_000,
  });
  const d = info.data;
  const st = d?.stats;
  if (!enabled) return null;
  const empty = info.isError ? "unavailable" : "—";

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-6">
      <StatCard title="MAS info" icon={masLogo}>
        <Row label="Total supply" value={d && `${MAS(d.total_supply)} MAS`} empty={empty} />
        <Row label="Circulating supply" value={d && `${MAS(d.circulating_supply)} MAS`} empty={empty} />
      </StatCard>
      <StatCard title="Staking on mainnet">
        <Row label="Staked MAS" value={st && `${MAS(st.staked_mas)} MAS`} empty={empty} />
        <Row label="Roll price" value={st && `${MAS(Number(st.roll_price))} MAS`} empty={empty} />
        <Row label="Staked rolls" value={st && MAS(st.staked_rolls)} empty={empty} />
        <Row label="Total stakers" value={st && MAS(st.total_stakers_mainnet)} empty={empty} />
      </StatCard>
      <CycleCard stats={st} empty={empty} />
    </div>
  );
}

function CycleCard({
  stats,
  empty,
}: {
  stats: MassaInfo["stats"] | undefined;
  empty: string;
}) {
  // 1 s tick so the progress bar and countdown move between refetches.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const start = stats?.current_cycle_time ?? 0;
  const end = stats?.next_cycle_time ?? 0;
  const pct = end > start ? Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100)) : 0;
  const left = Math.max(0, Math.round((end - now) / 1000));
  const fmt = (ms: number) => dayjs(ms).format("MMM D, HH:mm");

  return (
    <StatCard
      title="Current cycle"
      action={
        <span className="text-xl sm:text-2xl font-semibold tabular-nums text-fg">
          {stats ? MAS(stats.current_cycle) : "—"}
        </span>
      }
    >
      <Row label="Start time" value={stats && fmt(start)} empty={empty} />
      <Row label="End time" value={stats && fmt(end)} empty={empty} />
      <div className="mt-3">
        <div
          className="h-1.5 rounded-full bg-panel2 overflow-hidden"
          role="progressbar"
          aria-label="Cycle progress"
          aria-valuenow={Math.round(pct)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
        </div>
        <div className="text-xs text-muted mt-2">
          {stats ? `${Math.floor(left / 60)} min ${left % 60} s left` : "\u00a0"}
        </div>
      </div>
    </StatCard>
  );
}

function StatCard({
  title,
  icon,
  action,
  children,
}: {
  title: string;
  icon?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="card flex flex-col gap-2 min-w-0">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 text-sm font-medium text-muted">
          {icon && <img src={icon} alt="" width="20" height="20" />}
          {title}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Row({
  label,
  value,
  empty,
}: {
  label: string;
  value: string | undefined;
  empty: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-muted">{label}</span>
      <span className="font-medium tabular-nums text-right">{value ?? empty}</span>
    </div>
  );
}
